import { useEffect, useState, useCallback } from "react";
import { adminApi, formatApiError } from "./AuthContext";
import LineupManager from "./LineupManager";
import {
  centsToDollars,
  dollarsToCents,
  utcIsoToDenverDisplay,
  denverLocalInputToUtcIso,
  utcIsoToDenverLocalInput,
} from "./format";

const STATUSES = ["draft", "published", "completed"];

export default function EventsPanel() {
  const [events, setEvents] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await adminApi.get("/events");
      setEvents(data.items || []);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <section data-testid="admin-events-panel" style={{ marginTop: 36 }}>
      <h2 style={{ fontSize: 16, letterSpacing: 1, margin: "0 0 16px 0" }}>EVENTS</h2>
      <CreateEventForm onCreated={load} />
      {err && <Err msg={err} testid="admin-events-error" />}
      {loading ? (
        <div>Loading events…</div>
      ) : events.length === 0 ? (
        <div style={{ opacity: 0.6, marginTop: 14 }}>No events yet.</div>
      ) : (
        <table data-testid="admin-events-table" style={tableStyle}>
          <thead>
            <tr style={trHead}>
              <th style={th}>NAME</th>
              <th style={th}>DATE (MT)</th>
              <th style={th}>VENUE</th>
              <th style={th}>STATUS</th>
              <th style={th}></th>
            </tr>
          </thead>
          <tbody>
            {events.map((e) => (
              <EventRow
                key={e.id}
                event={e}
                open={openId === e.id}
                onToggle={() => setOpenId(openId === e.id ? null : e.id)}
                onChanged={load}
              />
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function EventRow({ event, open, onToggle, onChanged }) {
  return (
    <>
      <tr data-testid={`admin-event-row-${event.id}`} style={trBody}>
        <td style={td}>{event.name}</td>
        <td style={td}>{event.event_date}</td>
        <td style={td}>{event.venue}</td>
        <td style={td}>
          <Status status={event.status} />
        </td>
        <td style={{ ...td, textAlign: "right" }}>
          <button
            data-testid={`admin-event-open-${event.id}`}
            onClick={onToggle}
            style={btnSecondary}
          >
            {open ? "Close" : "Manage"}
          </button>
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={5} style={{ padding: 0, background: "#0d1731" }}>
            <EventEditor event={event} onChanged={onChanged} />
          </td>
        </tr>
      )}
    </>
  );
}

function EventEditor({ event, onChanged }) {
  const [name, setName] = useState(event.name);
  const [eventDate, setEventDate] = useState(event.event_date);
  const [venue, setVenue] = useState(event.venue);
  const [status, setStatus] = useState(event.status);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    setErr("");
    try {
      await adminApi.patch(`/events/${event.id}`, {
        name: name.trim(),
        event_date: eventDate,
        venue: venue.trim(),
        status,
      });
      onChanged();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ padding: 18, borderTop: "1px solid #14213D" }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "2fr 1fr 2fr 1fr auto",
          gap: 10,
          alignItems: "end",
        }}
      >
        <Field label="Name">
          <input
            data-testid={`event-edit-name-${event.id}`}
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={inputStyle}
          />
        </Field>
        <Field label="Event date (MT)">
          <input
            data-testid={`event-edit-date-${event.id}`}
            type="date"
            value={eventDate}
            onChange={(e) => setEventDate(e.target.value)}
            style={inputStyle}
          />
        </Field>
        <Field label="Venue">
          <input
            data-testid={`event-edit-venue-${event.id}`}
            value={venue}
            onChange={(e) => setVenue(e.target.value)}
            style={inputStyle}
          />
        </Field>
        <Field label="Status">
          <select
            data-testid={`event-edit-status-${event.id}`}
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            style={inputStyle}
          >
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </Field>
        <button
          data-testid={`event-edit-save-${event.id}`}
          onClick={save}
          disabled={busy}
          style={{ ...btnPrimary, height: 36 }}
        >
          {busy ? "Saving…" : "Save"}
        </button>
      </div>
      {err && <Err msg={err} testid={`event-edit-error-${event.id}`} />}
      <TiersManager eventId={event.id} />
      <LineupManager eventId={event.id} />
    </div>
  );
}

function TiersManager({ eventId }) {
  const [tiers, setTiers] = useState([]);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await adminApi.get(`/events/${eventId}/tiers`);
      setTiers(data.items || []);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div style={{ marginTop: 22 }}>
      <h3 style={{ fontSize: 13, letterSpacing: 1, margin: "0 0 10px 0", opacity: 0.8 }}>
        TICKET TIERS
      </h3>
      <CreateTierForm eventId={eventId} onCreated={load} />
      {err && <Err msg={err} testid={`tiers-error-${eventId}`} />}
      {loading ? (
        <div>Loading tiers…</div>
      ) : tiers.length === 0 ? (
        <div style={{ opacity: 0.6, marginTop: 10, fontSize: 13 }}>No tiers yet.</div>
      ) : (
        <table data-testid={`tiers-table-${eventId}`} style={{ ...tableStyle, marginTop: 12 }}>
          <thead>
            <tr style={trHead}>
              <th style={th}>NAME</th>
              <th style={th}>PRICE</th>
              <th style={th}>QTY TOTAL</th>
              <th style={th}>SOLD</th>
              <th style={th}>SALES WINDOW (MT)</th>
              <th style={th}>ACTIVE</th>
              <th style={th}></th>
            </tr>
          </thead>
          <tbody>
            {tiers.map((t) => (
              <TierRow key={t.id} tier={t} onChanged={load} />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function TierRow({ tier, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(tier.name);
  const [priceDollars, setPriceDollars] = useState(centsToDollars(tier.price_cents));
  const [quantityTotal, setQuantityTotal] = useState(tier.quantity_total);
  const [salesStart, setSalesStart] = useState(utcIsoToDenverLocalInput(tier.sales_start));
  const [salesEnd, setSalesEnd] = useState(utcIsoToDenverLocalInput(tier.sales_end));
  const [active, setActive] = useState(tier.active);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setErr("");
    const cents = dollarsToCents(priceDollars);
    if (cents == null || isNaN(cents)) {
      setErr("Price must look like 25 or 25.00.");
      return;
    }
    setBusy(true);
    try {
      await adminApi.patch(`/tiers/${tier.id}`, {
        name: name.trim(),
        price_cents: cents,
        quantity_total: parseInt(quantityTotal, 10),
        sales_start: denverLocalInputToUtcIso(salesStart),
        sales_end: denverLocalInputToUtcIso(salesEnd),
        active,
      });
      setEditing(false);
      onChanged();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!editing) {
    return (
      <tr data-testid={`tier-row-${tier.id}`} style={trBody}>
        <td style={td}>{tier.name}</td>
        <td style={td} data-testid={`tier-price-${tier.id}`}>
          ${centsToDollars(tier.price_cents)}
        </td>
        <td style={td}>{tier.quantity_total}</td>
        <td style={td} data-testid={`tier-sold-${tier.id}`}>
          {tier.quantity_sold} <span style={{ opacity: 0.5, fontSize: 11 }}>(read-only)</span>
        </td>
        <td style={td}>
          {utcIsoToDenverDisplay(tier.sales_start)}
          <br />
          {utcIsoToDenverDisplay(tier.sales_end)}
        </td>
        <td style={td}>
          <span style={{ color: tier.active ? "#5dd6a4" : "#ff6b6b" }}>
            {tier.active ? "yes" : "no"}
          </span>
        </td>
        <td style={{ ...td, textAlign: "right" }}>
          <button
            data-testid={`tier-edit-${tier.id}`}
            onClick={() => setEditing(true)}
            style={{ ...btnSecondary, padding: "4px 10px", fontSize: 12 }}
          >
            Edit
          </button>
        </td>
      </tr>
    );
  }

  return (
    <tr style={trBody}>
      <td style={td}>
        <input value={name} onChange={(e) => setName(e.target.value)} style={inputStyle} />
      </td>
      <td style={td}>
        <input
          data-testid={`tier-edit-price-${tier.id}`}
          value={priceDollars}
          onChange={(e) => setPriceDollars(e.target.value)}
          style={inputStyle}
          placeholder="25.00"
        />
      </td>
      <td style={td}>
        <input
          type="number"
          min="1"
          value={quantityTotal}
          onChange={(e) => setQuantityTotal(e.target.value)}
          style={inputStyle}
        />
      </td>
      <td style={td} data-testid={`tier-sold-edit-${tier.id}`}>
        {tier.quantity_sold}{" "}
        <span style={{ opacity: 0.5, fontSize: 11 }}>(read-only)</span>
      </td>
      <td style={td}>
        <input
          type="datetime-local"
          value={salesStart}
          onChange={(e) => setSalesStart(e.target.value)}
          style={inputStyle}
        />
        <input
          type="datetime-local"
          value={salesEnd}
          onChange={(e) => setSalesEnd(e.target.value)}
          style={{ ...inputStyle, marginTop: 4 }}
        />
      </td>
      <td style={td}>
        <label>
          <input
            type="checkbox"
            checked={active}
            onChange={(e) => setActive(e.target.checked)}
          />{" "}
          active
        </label>
      </td>
      <td style={{ ...td, textAlign: "right" }}>
        <button
          data-testid={`tier-save-${tier.id}`}
          onClick={save}
          disabled={busy}
          style={{ ...btnPrimary, padding: "4px 10px", fontSize: 12 }}
        >
          {busy ? "Saving…" : "Save"}
        </button>
        <div>
          <button
            onClick={() => setEditing(false)}
            style={{ ...btnSecondary, padding: "2px 8px", fontSize: 11, marginTop: 4 }}
          >
            Cancel
          </button>
        </div>
        {err && <div style={{ color: "#ff6b6b", fontSize: 11, marginTop: 4 }}>{err}</div>}
      </td>
    </tr>
  );
}

function CreateEventForm({ onCreated }) {
  const [name, setName] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [venue, setVenue] = useState("");
  const [status, setStatus] = useState("draft");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await adminApi.post("/events", {
        name: name.trim(),
        event_date: eventDate,
        venue: venue.trim(),
        status,
      });
      setName("");
      setEventDate("");
      setVenue("");
      setStatus("draft");
      onCreated();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="admin-create-event-form"
      style={{
        display: "grid",
        gridTemplateColumns: "2fr 1fr 2fr 1fr auto",
        gap: 10,
        alignItems: "end",
        padding: 14,
        background: "#101C35",
        border: "1px solid #14213D",
      }}
    >
      <Field label="Name">
        <input
          data-testid="event-create-name"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          style={inputStyle}
        />
      </Field>
      <Field label="Event date (MT)">
        <input
          data-testid="event-create-date"
          type="date"
          required
          value={eventDate}
          onChange={(e) => setEventDate(e.target.value)}
          style={inputStyle}
        />
      </Field>
      <Field label="Venue">
        <input
          data-testid="event-create-venue"
          required
          value={venue}
          onChange={(e) => setVenue(e.target.value)}
          style={inputStyle}
        />
      </Field>
      <Field label="Status">
        <select
          data-testid="event-create-status"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          style={inputStyle}
        >
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </Field>
      <button
        data-testid="event-create-submit"
        type="submit"
        disabled={busy}
        style={{ ...btnPrimary, height: 36 }}
      >
        {busy ? "Adding…" : "Add Event"}
      </button>
      {err && (
        <div style={{ gridColumn: "1 / -1", color: "#ff6b6b" }} data-testid="event-create-error">
          {err}
        </div>
      )}
    </form>
  );
}

function CreateTierForm({ eventId, onCreated }) {
  const [name, setName] = useState("");
  const [price, setPrice] = useState("");
  const [qty, setQty] = useState("");
  const [salesStart, setSalesStart] = useState("");
  const [salesEnd, setSalesEnd] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    const cents = dollarsToCents(price);
    if (cents == null || isNaN(cents)) {
      setErr("Price must look like 25 or 25.00.");
      return;
    }
    setBusy(true);
    try {
      await adminApi.post(`/events/${eventId}/tiers`, {
        name: name.trim(),
        price_cents: cents,
        quantity_total: parseInt(qty, 10),
        sales_start: denverLocalInputToUtcIso(salesStart),
        sales_end: denverLocalInputToUtcIso(salesEnd),
      });
      setName("");
      setPrice("");
      setQty("");
      setSalesStart("");
      setSalesEnd("");
      onCreated();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid={`tier-create-form-${eventId}`}
      style={{
        display: "grid",
        gridTemplateColumns: "1.4fr 0.8fr 0.8fr 1.4fr 1.4fr auto",
        gap: 10,
        alignItems: "end",
        padding: 12,
        background: "#0A0F1F",
        border: "1px solid #14213D",
      }}
    >
      <Field label="Tier name">
        <input
          data-testid={`tier-create-name-${eventId}`}
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          style={inputStyle}
        />
      </Field>
      <Field label="Price ($)">
        <input
          data-testid={`tier-create-price-${eventId}`}
          required
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          placeholder="25.00"
          style={inputStyle}
        />
      </Field>
      <Field label="Qty total">
        <input
          data-testid={`tier-create-qty-${eventId}`}
          required
          type="number"
          min="1"
          value={qty}
          onChange={(e) => setQty(e.target.value)}
          style={inputStyle}
        />
      </Field>
      <Field label="Sales start (MT)">
        <input
          data-testid={`tier-create-start-${eventId}`}
          required
          type="datetime-local"
          value={salesStart}
          onChange={(e) => setSalesStart(e.target.value)}
          style={inputStyle}
        />
      </Field>
      <Field label="Sales end (MT)">
        <input
          data-testid={`tier-create-end-${eventId}`}
          required
          type="datetime-local"
          value={salesEnd}
          onChange={(e) => setSalesEnd(e.target.value)}
          style={inputStyle}
        />
      </Field>
      <button
        data-testid={`tier-create-submit-${eventId}`}
        type="submit"
        disabled={busy}
        style={{ ...btnPrimary, height: 36 }}
      >
        {busy ? "Adding…" : "Add Tier"}
      </button>
      {err && (
        <div
          style={{ gridColumn: "1 / -1", color: "#ff6b6b" }}
          data-testid={`tier-create-error-${eventId}`}
        >
          {err}
        </div>
      )}
    </form>
  );
}

function Status({ status }) {
  const colors = {
    draft: "#9aa8c4",
    published: "#5dd6a4",
    completed: "#E5B25D",
  };
  return (
    <span
      style={{
        padding: "2px 8px",
        background: colors[status] || "#9aa8c4",
        color: "#0A0F1F",
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 1,
      }}
    >
      {String(status || "").toUpperCase()}
    </span>
  );
}

function Field({ label, children }) {
  return (
    <label style={{ fontSize: 11, letterSpacing: 1, opacity: 0.7 }}>
      {label.toUpperCase()}
      {children}
    </label>
  );
}

function Err({ msg, testid }) {
  return (
    <div data-testid={testid} style={{ color: "#ff6b6b", margin: "10px 0", fontSize: 13 }}>
      {msg}
    </div>
  );
}

const inputStyle = {
  width: "100%",
  padding: "8px 10px",
  background: "#0A0F1F",
  border: "1px solid #2a3556",
  color: "#fff",
  borderRadius: 2,
  marginTop: 4,
  fontSize: 13,
  boxSizing: "border-box",
};
const btnPrimary = {
  padding: "8px 14px",
  background: "#E5B25D",
  color: "#0A0F1F",
  border: 0,
  fontWeight: 700,
  cursor: "pointer",
};
const btnSecondary = {
  padding: "8px 14px",
  background: "transparent",
  color: "#fff",
  border: "1px solid #2a3556",
  cursor: "pointer",
};
const tableStyle = { width: "100%", borderCollapse: "collapse", marginTop: 16 };
const th = { padding: "10px 8px", fontWeight: 600 };
const td = { padding: "10px 8px", fontSize: 14, verticalAlign: "top" };
const trHead = { textAlign: "left", fontSize: 11, opacity: 0.6 };
const trBody = { borderTop: "1px solid #14213D" };
